import React, { ComponentType, useMemo, useState } from 'react';

import { GMetrixStackedProgressBar, GMetrixStackedProgressBarItem } from 'components/GMetrixStackedProgressBar/GMetrixStackedProgressBar';
import { ConditionalWrapper } from 'components/conditional_wrapper/ConditionalWrapper';
import { GMetrixProgressBar } from 'components/GMetrixProgressBar/GMetrixProgressBar';
import { GenerateReport } from 'components/GenerateReport/GenerateReport';
import { TruncatedText } from 'components/truncated_text/TruncatedText';
import { GMetrixButton } from 'components/GMetrixButton/GMetrixButton';
import { GMetrixDialog } from 'components/GMetrixDialog/GMetrixDialog';
import { capitalizeEveryFirstLetter, getFormattedDate } from 'utils';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import { Report, ReportApi } from 'apis/ReportApi';
import { useIntl } from 'react-intl';
import { LicenseType } from 'types';

import 'components/GMetrixTable/components/column/styles/GmetrixTableColumnCell.scss';

export interface GMetrixTableCellProps<T> {
    className?: string;
    colSpan?: number;
    dataItem: T;
    field?: string;
    style?: React.CSSProperties;
}

export interface GMetrixTableColumnProps<T> {
    cell?: ComponentType<GMetrixTableCellProps<T>>;
    cellNoWrap?: boolean;
    cellTitle?: (dataItem: T) => string;
    columnNumber?: number;
    columnsDropdownSelected?: boolean;
    dataType?: string;
    defaultSortDir?: 'asc' | 'desc';
    editCells?: boolean;
    field?:
    | Extract<keyof T, string>
    | `${Extract<keyof T, string>}.${string}`
    | `${Extract<keyof T, string>}[${number}].${string}`;
    grabTableData?: (dataItem: T, originalRow: T) => void;
    headerCell?: React.ComponentType<GMetrixTableHeaderCellProps>;
    hidden?: boolean;
    lockColumns?: boolean;
    locked?: boolean;
    minResizableWidth?: number;
    setCurrentSubReport?: React.Dispatch<React.SetStateAction<Report | undefined>> | undefined;
    setTableKey?: React.Dispatch<React.SetStateAction<string>>;
    sortable?: boolean;

    /**
     * Override the field name sent to the server when sorting this column.
     * Use this when the frontend `field` name differs from the backend sort column name.
     * Only applies when `serverSidePagination` is enabled on the parent table.
     */
    sortField?: string;
    tableData?: T[];
    title?: string;
    width?: number | string;
}

export interface GMetrixTableHeaderCellProps {
    field?: string;
    title?: string;
}

interface GMetrixTableColumnRenderProps<T> extends GMetrixTableColumnProps<T> {
    dataItem: T;
    sortDirection?: 'asc' | 'desc';
    sortField?: string;
}

export function GMetrixTableColumnCell<T>({
    cell: CustomCell,
    cellNoWrap = true,
    cellTitle,
    dataItem,
    editCells,
    field,
    grabTableData,
    setCurrentSubReport
}: GMetrixTableColumnRenderProps<T>) {

    const [showModal, setShowModal] = useState(false);
    const [embededURL, setEmbededURL] = useState('');
    const { formatMessage } = useIntl();

    // When a details action targets a report route (e.g. report 6 -> report 75),
    // render the report inline as a subcomponent with its filters pre-filled from
    // the URL, rather than embedding a full second app instance in an iframe.
    const subReport = useMemo(() => {

        if (!embededURL) return null;
        try {

            const url = new URL(embededURL, window.location.origin);
            const pathMatch = url.pathname.match(/\/reports\/(\d+)/i);

            // The report id must come from the row's own action URL, never the parent
            // page — so non-report actions still fall through to the iframe below.
            const reportIdRaw = pathMatch ? pathMatch[1] : new URLSearchParams(url.search).get('reportId');
            const parsedReportId = reportIdRaw ? Number(reportIdRaw) : NaN;
            if (!parsedReportId || Number.isNaN(parsedReportId)) return null;

            // Detail modals open the bespoke report 68 experience; report 75 (its
            // generic-table stand-in) stays reachable only by direct URL.
            const DETAIL_REPORT_REMAP: Record<number, number> = { 75: 68 };
            const resolvedReportId = DETAIL_REPORT_REMAP[parsedReportId] ?? parsedReportId;

            // Merge the parent report page's query params (scope, StartEndDate, other
            // filters) with the row's action-URL params so the detail report has the
            // full context it needs to run. Keys are lower-cased and the action URL
            // wins, so the row's ScopeObjectId (the detail key) overrides the parent
            // page's scope while inherited filters (e.g. the date range) survive.
            const params = new URLSearchParams();
            new URLSearchParams(window.location.search).forEach((v, k) => params.set(k.toLowerCase(), v));

            const pageScopeObjectId = null;

            // Feed the row's own primitive fields (e.g. a hidden StudentId column) so
            // detail-report filters the DB action URL doesn't carry still resolve. Each
            // field is also set under a normalized key (non-alphanumerics stripped) so
            // column/filter naming variants like "Student Id" vs "StudentId" still match.
            if (dataItem && typeof dataItem === 'object') {

                const row = dataItem as Record<string, unknown>;

                Object.entries(row).forEach(([k, v]) => {

                    if (v !== null && (typeof v === 'string' || typeof v === 'number' || typeof v === 'boolean')) {

                        params.set(k.toLowerCase(), String(v));
                        params.set(k.toLowerCase().replace(/[^a-z0-9]/g, ''), String(v));

                    }

                });

                // Detail-report filters are often keyed by entity name ("Student",
                // "Course", "Classrooms") while row columns carry id suffixes
                // (StudentProfileId, CourseId, StudentGroupId); alias each row id to
                // every name the filter might use so it resolves regardless.
                const aliasRowValue = (keyPattern: RegExp, aliases: string[]) => {

                    const key = Object.keys(row).find(k => keyPattern.test(k));
                    const val = key ? row[key] : undefined;
                    if (val !== null && val !== undefined && typeof val !== 'object') {

                        aliases.forEach(a => params.set(a, String(val)));

                    }

                };

                aliasRowValue(/^student[ _]?(profile)?[ _]?ids?$/i, ['student', 'students', 'studentid', 'studentprofileid']);
                aliasRowValue(/^course[ _]?ids?$/i, ['course', 'courses', 'courseid']);
                aliasRowValue(/^(classroom|studentgroup|group)s?[ _]?ids?$/i, ['classroom', 'classrooms', 'classroomid', 'classroomsid', 'studentgroupid', 'studentgroupsid', 'groupid']);

            }

            new URLSearchParams(url.search).forEach((v, k) => params.set(k.toLowerCase(), v));

            // The action URL's ScopeObjectId is the row's detail key (e.g. CourseStudent id),
            // which is the wrong scope for a partner-scoped detail report like 68 — running
            // with it returns no rows. Restore the parent page's scope for those reports,
            // whether they were remapped or the action URL targets them directly.
            const PARTNER_SCOPED_DETAIL_REPORTS = [68];
            if (pageScopeObjectId && (resolvedReportId !== parsedReportId || PARTNER_SCOPED_DETAIL_REPORTS.includes(resolvedReportId))) {

                params.set('scopeobjectid', pageScopeObjectId);

            }

            // No classroom on the row: default to 'all' so the classroom filter expands to
            // every active classroom for the scope instead of leaving the report unready.
            if (!params.get('classroom') && !params.get('classrooms')) {

                params.set('classroom', 'all');
                params.set('classrooms', 'all');

            }

            return {
                reportId: resolvedReportId,
                scopeId: Number(params.get('scopeid') ?? 0),
                scopeObjectId: Number(params.get('scopeobjectid') ?? 0),
                search: `?${params.toString()}`
            };

        } catch {

            return null;

        }

    }, [embededURL, dataItem]);

    // DO NOT DELETE OpenURL or RunSubReport
    const OpenURL = (url: string, target: string) => {

        if (target === 'modal') {

            setShowModal(true);
            setEmbededURL(url);

        } else if (target === 'current') {

            window.open(url, '_self');

        } else {

            window.open(url, target);

        }

    };

    const RunSubReport = (scopeObjectId: number, reportId: number, subReportComponentId: number) => {

        ReportApi.GetReportLayout(reportId, scopeObjectId, 1, true).then(setCurrentSubReport).catch(console.error);

    };

    const formatData = (cellValue: any) => {

        const formattedData: GMetrixStackedProgressBarItem[] = [];
        cellValue.map((dataItem: any) => {

            formattedData.push({ color: dataItem.Color, title: dataItem.Title, value: Number(dataItem.Share) });

        });
        return formattedData;

    };

    const handleCellValue = (cellValue: any, fieldName: string | undefined, item: any) => {

        const dateRegex = /[0-9][0-9][0-9][0-9][-/][0-9][0-9][-/][0-9][0-9]T[0-9][0-9]:[0-9][0-9]:[0-9][0-9]/;
        if (cellValue === null || cellValue === undefined) {

            return cellValue;

        }
        if (typeof cellValue === 'object') {

            switch (cellValue.Type) {

                case 'ErrorState':
                    return (
                        <OverlayTrigger
                            overlay={(
                                <Tooltip>
                                    {cellValue.HoverText}
                                </Tooltip>
                            )}
                            placement="top"
                        >
                            <div
                                style={{
                                    backgroundColor: '#FED8D7',
                                    border: '2px solid #E44A4C',
                                    borderRadius: '5px',
                                    lineHeight: '30px'
                                }}
                                contentEditable={editCells}
                            >
                                {cellValue.Title}
                            </div>
                        </OverlayTrigger>
                    );
                case 'Bool':
                    return (
                        <div title={cellValue.HoverText ? cellValue.HoverText : ''}>
                            <span className="booleanDot" style={{ backgroundColor: cellValue.Color }}></span>
                        </div>
                    );
                case 'ProgressBar':
                    if (cellValue.Share === '-1') {

                        return null;

                    }
                    return <GMetrixProgressBar className="gmetrixTableColumn" dataItem={{ Title: '', ...cellValue }} />;
                case 'Action':
                    if (cellValue.Action === 'none') {

                        return;

                    }
                    return (
                        <>
                            <GMetrixDialog
                                actionButtonClick={() => setShowModal(false)}
                                actionButtonDisabled={false}
                                actionButtonText={formatMessage({ id: 'close.99e01fb5', defaultMessage: 'Close' })}
                                dialogLabel={cellValue.Title}
                                onCancel={() => setShowModal(false)}
                                show={showModal}
                                showCancelButton={false}
                            >
                                {showModal && (subReport ? (
                                    <GenerateReport
                                        autoRunReport
                                        showNoFilters
                                        reportId={subReport.reportId}
                                        scopeId={subReport.scopeId}
                                        scopeObjectId={subReport.scopeObjectId}
                                        urlParamsOverride={subReport.search}
                                    />
                                ) : (
                                    <iframe
                                        src={embededURL}
                                        style={{ height: '80vh', overflow: 'none', width: '100%' }}
                                    ></iframe>
                                ))}
                            </GMetrixDialog>
                            <GMetrixButton
                                style={{
                                    backgroundColor: cellValue.Color ? cellValue.Color : undefined,
                                    border: cellValue.TextColor ? `2px solid ${cellValue.TextColor}` : undefined,
                                    color: cellValue.TextColor ? cellValue.TextColor : undefined,
                                    cursor: cellValue.Action ? 'pointer' : 'default'
                                }}
                                className="text-center"
                                disabled={!cellValue.Action}
                                onClick={() => eval(cellValue.Action)}
                                title={cellValue.HoverText ? cellValue.HoverText : cellValue.Title}
                                variant="primary"
                            >
                                {cellValue.Title}
                            </GMetrixButton>
                        </>
                    );
                default:
                    break;

            }
            if (cellValue.length > 0 && cellValue[0].Type === 'StackedProgressBar') {

                return (
                    <div style={{ height: '50px' }}>
                        <GMetrixStackedProgressBar data={formatData(cellValue)} />
                    </div>
                );

            }

        } else if (typeof cellValue === 'number') {

            switch (fieldName) {

                case 'PracticeTestUses':
                    if (item.PracticeTestUses === -1) return formatMessage({ id: 'na.022d6995', defaultMessage: 'N/A' });
                    if (item.LicenseType === LicenseType.Individual) {

                        return item.PracticeTestUses;

                    } else {

                        if (item.LicensePracticeTestsMaxUses === -1) {

                            return formatMessage({ id: 'unlimited.b981eeab', defaultMessage: 'Unlimited', description: 'Formulario' });

                        } else {

                            return item.PracticeTestUses;

                        }

                    }
                case 'CourseUses':
                    if (item.CourseUses === -1) return formatMessage({ id: 'na.022d6995', defaultMessage: 'N/A' });
                    if (item.LicenseType === LicenseType.Individual) {

                        return item.CourseUses;

                    } else {

                        if (item.LicenseCoursesMaxUses === -1) {

                            return formatMessage({ id: 'unlimited.b981eeab', defaultMessage: 'Unlimited', description: 'Formulario' });

                        } else {

                            return item.CourseUses;

                        }

                    }
                case 'EbookUses':
                    if (item.EbookUses === -1) return formatMessage({ id: 'na.022d6995', defaultMessage: 'N/A' });
                    if (item.LicenseType === LicenseType.Individual) {

                        return item.EbookUses;

                    } else {

                        if (item.LicenseEbookMaxUses === -1) {

                            return formatMessage({ id: 'unlimited.b981eeab', defaultMessage: 'Unlimited', description: 'Formulario' });

                        } else {

                            return item.EbookUses;

                        }

                    }

            }

        } else if (typeof cellValue === 'boolean') {

            let className = '';
            if (cellValue) {

                className = 'fa-solid fa-check text-success gmetrixTableColumn__true';

            } else {

                className = 'fa-solid fa-minus gmetrixTableColumn__false';

            }
            return <i className={className}></i>;

        } else if (typeof cellValue === 'string' && dateRegex.test(cellValue)) {

            if (cellValue === '0001-01-01T00:00:00') {

                return '';

            }
            return getFormattedDate(new Date(cellValue));

        }
        return (
            <div contentEditable={editCells}>
                <ConditionalWrapper
                    wrapper={children => (
                        <TruncatedText maxWidth="100%">
                            {children}
                        </TruncatedText>
                    )}
                    condition={cellNoWrap}
                >
                    {String(cellValue ?? '')}
                </ConditionalWrapper>
            </div>
        );

    };

    if (CustomCell) {

        return (
            <CustomCell
                dataItem={dataItem}
                field={field}
            />
        );

    }

    let cellValue: any;
    if (field != undefined && field != '' && field != null) {

        cellValue = (dataItem as any)[field as any];

        cellValue = (field as string).split('.').reduce((acc: any, key: string) => acc?.[key], dataItem as any);

    } else {

        cellValue = (dataItem as any)[field as any];

    }

    // Sort details rows if needed
    if (
        (dataItem as any).Details !== undefined &&
        (dataItem as any).Details.Data !== undefined &&
        (dataItem as any).Details.Data[0]?.SortValue !== undefined
    ) {

        (dataItem as any).Details.Data.sort((a: any, b: any) => a.Detail?.SortValue - b.Detail?.SortValue);

    }

    const titleText = cellTitle
        ? cellTitle(dataItem)
        : cellNoWrap
            ? `${cellValue !== null
                ? typeof cellValue === 'boolean'
                    ? capitalizeEveryFirstLetter(cellValue.toString())
                    : cellValue ?? ''
                : ''
            }`
            : undefined;

    return (
        <td
            onBlur={e => {

                // Only grab edited text for editable, primitive cells; never overwrite
                // structured values (Action buttons, ProgressBars, etc.) with innerText.
                if (grabTableData && editCells && (cellValue === null || typeof cellValue !== 'object')) {

                    const originalRow = { ...dataItem };
                    delete (originalRow as any).selected;
                    (dataItem as any)[field as any] = e.currentTarget.innerText;
                    grabTableData(dataItem, originalRow);

                }

            }}
            aria-label={titleText}
            className={`${cellNoWrap ? 'gmetrixTableColumn__table-cell--no-wrap' : ''}`}
            role="gridcell"
        >
            {handleCellValue(cellValue, field, dataItem)}
        </td>
    );

}
